import { supabase } from '../client/browser'
import { v4 as uuidv4 } from 'uuid'

export interface CreateMeetingData {
  title: string
  description?: string
  type: string
  category: string
  start: string
  end: string
  meetingLink?: string
  participantIds: string[]
  createdById: string
}

// Create a factory function that accepts a supabase client
export const createMeetingService = (supabaseClient: any) => ({
  async createMeetingWithAvailability(data: CreateMeetingData) {
    try {
      // Start a transaction by using RPC
      const { data: result, error } = await supabaseClient.rpc('create_meeting_with_availability', {
        meeting_data: {
          title: data.title,
          description: data.description,
          type: data.type,
          category: data.category,
          start: data.start,
          end: data.end,
          meetingLink: data.meetingLink,
          participantIds: data.participantIds,
          createdById: data.createdById
        }
      })

      if (error) throw error
      return result
    } catch (error) {
      console.error('Error creating meeting with availability:', error)
      throw error
    }
  },

  async createMeetingWithAvailabilityFallback(data: CreateMeetingData) {
    try {
      console.log('[meetingService.createMeetingWithAvailabilityFallback] Starting with:', data)
      
      // First, create the meeting
      console.log('[meetingService] Creating meeting record...')
      const meetingId = uuidv4()
      const now = new Date().toISOString()
      const { data: meeting, error: meetingError } = await supabaseClient
        .from('meetings')
        .insert({
          id: meetingId,
          title: data.title,
          description: data.description,
          type: data.type,
          category: data.category,
          start: data.start,
          end: data.end,
          meetingLink: data.meetingLink,
          participantIds: data.participantIds,
          createdById: data.createdById,
          createdAt: now,
          updatedAt: now
        })
        .select()
        .single()

      console.log('[meetingService] Meeting creation result:', { meeting, meetingError })
      
      if (meetingError) throw meetingError

      // Then create availability blocks for each participant
      console.log('[meetingService] Creating availability blocks for', data.participantIds.length, 'participants')
      // Meeting times are already in UTC from ScheduleMeetingsPage
      const availabilityBlocks = data.participantIds.map(proId => ({
        id: uuidv4(),
        proId,
        meetingId: meeting.id,
        start: data.start,  // Already in UTC
        end: data.end,      // Already in UTC
        isBlock: true,
        isPD: data.type === 'knowledge_network' || data.type === 'levelup_lab',
        notes: `Meeting: ${data.title}`,
        createdAt: now,
        updatedAt: now
      }))

      const { error: availabilityError } = await supabaseClient
        .from('availability')
        .insert(availabilityBlocks)

      if (availabilityError) {
        // If availability creation fails, delete the meeting to maintain consistency
        await supabaseClient.from('meetings').delete().eq('id', meeting.id)
        throw availabilityError
      }

      return meeting
    } catch (error) {
      console.error('Error creating meeting with availability (fallback):', error)
      throw error
    }
  },

  async getParticipantIds(selectionType: 'cohort' | 'employment', selectedValue: string): Promise<string[]> {
    try {
      console.log('[meetingService.getParticipantIds] Starting with:', { selectionType, selectedValue })
      
      // No need to check auth - this query doesn't require it
      
      let query = supabaseClient
        .from('pros')
        .select('id')
        .is('deletedAt', null)

      // Apply filters based on selection
      if (selectionType === 'cohort') {
        // For cohorts, include all members regardless of isActive status
        query = query.eq('cohort', selectedValue)
      } else {
        // For other selections, only include active therapists
        query = query.eq('isActive', true)
        // Handle group selections
        switch (selectedValue) {
          case 'Residents':
            query = query.eq('employmentStatus', 'RESIDENT')
            break
          case 'Supervisors':
            query = query.eq('isSupervisor', true)
            break
          case 'FTAs':
            query = query.eq('isTherapist', true).eq('employmentStatus', 'EMPLOYEE')
            break
          case 'All Employees':
            query = query.eq('employmentStatus', 'EMPLOYEE')
            break
          case 'All Therapists':
            query = query.eq('isTherapist', true)
            break
          case 'Everyone':
            // No additional filter needed
            break
          default:
            throw new Error(`Unknown group: ${selectedValue}`)
        }
      }

      console.log('[meetingService.getParticipantIds] Executing query...')
      
      // Execute the query directly without timeout
      const { data, error } = await query
      
      console.log('[meetingService.getParticipantIds] Query result:', { 
        dataCount: data?.length || 0, 
        error,
        sample: data?.[0]
      })

      if (error) {
        console.error('[meetingService.getParticipantIds] Query error:', error)
        throw new Error(`Failed to fetch participants: ${error.message}`)
      }

      const ids = data?.map((pro: any) => pro.id) || []
      console.log('[meetingService.getParticipantIds] Returning IDs:', ids)
      return ids
    } catch (error) {
      console.error('[meetingService.getParticipantIds] Error:', error)
      throw error
    }
  },

  async getMeetings() {
    try {
      const { data, error } = await supabaseClient
        .from('meetings')
        .select(`
          *,
          createdBy:pros!meetings_createdById_fkey(
            id,
            firstName,
            lastName
          )
        `)
        .order('start', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('[meetingService.getMeetings] Error:', error)
      throw error
    }
  },

  async deleteMeeting(meetingId: string) {
    try {
      // First delete all related availability blocks
      const { error: availabilityError } = await supabaseClient
        .from('availability')
        .delete()
        .eq('meetingId', meetingId)
      
      if (availabilityError) throw availabilityError
      
      // Then delete the meeting
      const { error: meetingError } = await supabaseClient
        .from('meetings')
        .delete()
        .eq('id', meetingId)
      
      if (meetingError) throw meetingError
      
      return { success: true }
    } catch (error) {
      console.error('[meetingService.deleteMeeting] Error:', error)
      throw error
    }
  }
})

// Keep the singleton for backward compatibility
export const meetingService = {
  /**
   * Create a meeting and block availability for all participants
   */
  async createMeetingWithAvailability(data: CreateMeetingData) {
    try {
      // Start a transaction by using RPC
      const { data: result, error } = await supabase.rpc('create_meeting_with_availability', {
        meeting_data: {
          title: data.title,
          description: data.description,
          type: data.type,
          category: data.category,
          start: data.start,
          end: data.end,
          meetingLink: data.meetingLink,
          participantIds: data.participantIds,
          createdById: data.createdById
        }
      })

      if (error) throw error
      return result
    } catch (error) {
      console.error('Error creating meeting with availability:', error)
      throw error
    }
  },

  /**
   * Create a meeting and manually handle availability blocks
   * (Fallback if RPC function doesn't exist)
   */
  async createMeetingWithAvailabilityFallback(data: CreateMeetingData) {
    try {
      console.log('[meetingService.createMeetingWithAvailabilityFallback] Starting with:', data)
      
      // First, create the meeting
      console.log('[meetingService] Creating meeting record...')
      const meetingId = uuidv4()
      const now = new Date().toISOString()
      const { data: meeting, error: meetingError } = await supabase
        .from('meetings')
        .insert({
          id: meetingId,
          title: data.title,
          description: data.description,
          type: data.type,
          category: data.category,
          start: data.start,
          end: data.end,
          meetingLink: data.meetingLink,
          participantIds: data.participantIds,
          createdById: data.createdById,
          createdAt: now,
          updatedAt: now
        })
        .select()
        .single()

      console.log('[meetingService] Meeting creation result:', { meeting, meetingError })
      
      if (meetingError) throw meetingError

      // Then create availability blocks for each participant
      console.log('[meetingService] Creating availability blocks for', data.participantIds.length, 'participants')
      // Meeting times are already in UTC from ScheduleMeetingsPage
      const availabilityBlocks = data.participantIds.map(proId => ({
        id: uuidv4(),
        proId,
        meetingId: meeting.id,
        start: data.start,  // Already in UTC
        end: data.end,      // Already in UTC
        isBlock: true,
        isPD: data.type === 'knowledge_network' || data.type === 'levelup_lab',
        notes: `Meeting: ${data.title}`,
        createdAt: now,
        updatedAt: now
      }))

      const { error: availabilityError } = await supabase
        .from('availability')
        .insert(availabilityBlocks)

      if (availabilityError) {
        // If availability creation fails, delete the meeting to maintain consistency
        await supabase.from('meetings').delete().eq('id', meeting.id)
        throw availabilityError
      }

      return meeting
    } catch (error) {
      console.error('Error creating meeting with availability (fallback):', error)
      throw error
    }
  },

  /**
   * Get participant IDs based on selection type and value
   */
  async getParticipantIds(selectionType: 'cohort' | 'employment', selectedValue: string, userId?: string): Promise<string[]> {
    try {
      console.log('[meetingService.getParticipantIds] Starting with:', { selectionType, selectedValue, userId })
      
      // userId is optional - only used for logging purposes
      // The actual query doesn't need auth since we're just listing pros
      
      let query = supabase
        .from('pros')
        .select('id')
        .is('deletedAt', null)

      // Apply filters based on selection
      if (selectionType === 'cohort') {
        // For cohorts, include all members regardless of isActive status
        query = query.eq('cohort', selectedValue)
      } else {
        // For other selections, only include active therapists
        query = query.eq('isActive', true)
        // Handle group selections
        switch (selectedValue) {
          case 'Residents':
            query = query.eq('employmentStatus', 'RESIDENT')
            break
          case 'Supervisors':
            query = query.eq('isSupervisor', true)
            break
          case 'FTAs':
            query = query.eq('isTherapist', true).eq('employmentStatus', 'EMPLOYEE')
            break
          case 'All Employees':
            query = query.eq('employmentStatus', 'EMPLOYEE')
            break
          case 'All Therapists':
            query = query.eq('isTherapist', true)
            break
          case 'Everyone':
            // No additional filter needed
            break
          default:
            throw new Error(`Unknown group: ${selectedValue}`)
        }
      }

      console.log('[meetingService.getParticipantIds] Executing query...')
      
      // Execute the query directly without timeout
      const { data, error } = await query
      
      console.log('[meetingService.getParticipantIds] Query result:', { 
        dataCount: data?.length || 0, 
        error,
        sample: data?.[0]
      })

      if (error) {
        console.error('[meetingService.getParticipantIds] Query error:', error)
        throw new Error(`Failed to fetch participants: ${error.message}`)
      }

      const ids = data?.map((pro: any) => pro.id) || []
      console.log('[meetingService.getParticipantIds] Returning IDs:', ids)
      return ids
    } catch (error) {
      console.error('[meetingService.getParticipantIds] Error:', error)
      throw error
    }
  },

  async getMeetings() {
    try {
      const { data, error } = await supabase
        .from('meetings')
        .select(`
          *,
          createdBy:pros!meetings_createdById_fkey(
            id,
            firstName,
            lastName
          )
        `)
        .order('start', { ascending: false })
      
      if (error) throw error
      return data || []
    } catch (error) {
      console.error('[meetingService.getMeetings] Error:', error)
      throw error
    }
  },

  async deleteMeeting(meetingId: string) {
    try {
      // First delete all related availability blocks
      const { error: availabilityError } = await supabase
        .from('availability')
        .delete()
        .eq('meetingId', meetingId)
      
      if (availabilityError) throw availabilityError
      
      // Then delete the meeting
      const { error: meetingError } = await supabase
        .from('meetings')
        .delete()
        .eq('id', meetingId)
      
      if (meetingError) throw meetingError
      
      return { success: true }
    } catch (error) {
      console.error('[meetingService.deleteMeeting] Error:', error)
      throw error
    }
  }
}